import { Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useCart } from '../state/cart';
import elenardShop from '../data/elenardShop.json';
import { slugFromProductUrl } from '../data/mockCatalog';

export function CartPage() {
  const { items, subtotal, setQty, removeItem, itemCount } = useCart();
  const navigate = useNavigate();

  const newInStore = useMemo(() => {
    const products = elenardShop?.products || [];
    return products.filter((p) => !p.outOfStock).slice(0, 3);
  }, []);

  return (
    <section className="section-padding elenardSectionTight">
      <div className="container">
        <div className="sec-head mb-30">
          <span className="sub-title main-color mb-10">Cart</span>
          <h3 className="fw-600 fz-50 text-u">
            Shopping <span className="fw-200">cart</span>
          </h3>
          <div className="opacity-8 fz-14 mt-10">{itemCount} item(s)</div>
        </div>

        {items.length === 0 ? (
          <>
            <div className="elenardCartEmpty item" style={{ padding: 18 }}>
              <h5 className="mb-10">Your cart is currently empty!</h5>
              <p className="opacity-8 mb-20">
                Browse the shop and add your favorite pieces.
              </p>
              <Link to="/shop" className="butn butn-md butn-bg main-colorbg radius-30">
                <span className="text">Return to shop</span>
              </Link>
            </div>

            {newInStore.length > 0 ? (
              <div className="mt-50">
                <div className="sec-head mb-30">
                  <span className="sub-title main-color mb-10">New in store</span>
                </div>
                <div className="row">
                  {newInStore.map((p) => {
                    const slug = slugFromProductUrl(p.url);
                    return (
                      <div key={p.url} className="col-lg-4 col-md-6 mb-30">
                        <div className="item elenardProductCard">
                          <div className="img">
                            <Link to={`/product/${slug}`}>
                              <img
                                src={p.image || '/assets/imgs/logo-light.svg'}
                                alt={p.name}
                                className="elenardProductImg"
                                loading="lazy"
                              />
                            </Link>
                          </div>
                          <div className="cont mt-20 elenardProductCont">
                            <h6 className="mb-10 elenardProductTitle">{p.name}</h6>
                            <div className="elenardPriceRow">
                              <div className="elenardPrice">{p.priceText || ''}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              <Link
                                to={`/product/${slug}`}
                                className="butn butn-sm butn-bord radius-30"
                              >
                                <span className="text">Read More</span>
                              </Link>
                              <Link
                                to="/shop"
                                className="butn butn-sm butn-bg main-colorbg radius-30"
                              >
                                <span className="text">Shop</span>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="elenardCartGrid">
            <div className="elenardCartItems item">
              <div className="elenardCartTableHead">
                <div>Product</div>
                <div className="hide-sm">Price</div>
                <div>Quantity</div>
                <div className="hide-sm">Total</div>
                <div></div>
              </div>

              {items.map((x) => {
                const slug = String(x.id || '').startsWith('shop:')
                  ? String(x.id).slice('shop:'.length)
                  : null;
                const productHref = slug ? `/product/${slug}` : null;
                const total = Number((x.qty * x.price).toFixed(2));
                const maxQty = x.maxQty ?? null;
                const stockLeft = maxQty ? Math.max(0, maxQty - x.qty) : null;

                return (
                  <div key={x.id} className="elenardCartRow">
                    <div className="elenardCartProductCell">
                      <div className="elenardCartThumb">
                        <img
                          src={x.image || '/assets/imgs/logo-light.svg'}
                          alt={x.name}
                          loading="lazy"
                        />
                      </div>
                      <div className="elenardCartProductMeta">
                        {productHref ? (
                          <Link to={productHref} className="elenardCartProductName">
                            {x.name}
                          </Link>
                        ) : (
                          <div className="elenardCartProductName">{x.name}</div>
                        )}
                        <div className="elenardCartMetaLine">${x.price} each</div>
                        {stockLeft !== null ? (
                          <div className="elenardCartMetaLine">
                            Stock left: <b>{stockLeft}</b>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="hide-sm elenardCartPrice">${x.price}</div>

                    <div className="elenardCartQty">
                      <label className="srOnly" htmlFor={`qty-${x.id}`}>
                        Quantity
                      </label>
                      <input
                        id={`qty-${x.id}`}
                        className="elenardCartQtyInput"
                        type="number"
                        min={1}
                        max={x.maxQty ?? undefined}
                        value={x.qty}
                        onChange={(e) => setQty(x.id, Number(e.target.value))}
                      />
                    </div>

                    <div className="hide-sm elenardCartTotal">${total}</div>

                    <div className="elenardCartActions">
                      <button
                        type="button"
                        className="butn butn-sm butn-bord radius-30"
                        onClick={() => removeItem(x.id)}
                        aria-label={`Remove ${x.name}`}
                      >
                        <span className="text">Remove</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="elenardCartSummary item">
              <div className="elenardCartSummaryInner">
                <h5 className="mb-10">Cart totals</h5>
                <div className="elenardCartSummaryRow">
                  <span className="opacity-8">Subtotal</span>
                  <span className="fw-600">${subtotal}</span>
                </div>
                <div className="elenardCartSummaryRow">
                  <span className="opacity-8">Shipping</span>
                  <span className="opacity-8">Calculated at checkout</span>
                </div>
                <div className="elenardCartSummaryDivider" />
                <div className="elenardCartSummaryRow">
                  <span className="fw-600">Total</span>
                  <span className="fw-700">${subtotal}</span>
                </div>

                <div className="mt-20" style={{ display: 'grid', gap: 10 }}>
                  <button
                    className="butn butn-md butn-bg main-colorbg radius-30"
                    type="button"
                    onClick={() => navigate('/checkout')}
                  >
                    <span className="text">Proceed to checkout</span>
                  </button>
                  <Link to="/shop" className="butn butn-md butn-bord radius-30">
                    <span className="text">Continue shopping</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

